import { useMemo } from "react";
import { fmt } from "@/lib/format";

// Chart palette matching legacy CHART_COLORS
const COLORS = {
  net_pos: "#22c55e",
  net_neg: "#ef4444",
  equity: "#3b82f6",
  safe_factor: "#eab308",
} as const;

// SVG viewBox dimensions
const W = 300;
const H = 80;
const Y_PAD = 6;

type Metric = "net" | "equity" | "safe_factor";

interface MiniChartProps {
  label: string;
  /** Raw values array (may contain nulls). */
  values: (number | null)[];
  metric: Metric;
  decimals: number;
  suffix?: string;
}

/** Build SVG path string from values array — memoized so re-polls are cheap. */
function buildPath(values: (number | null)[]): { d: string; min: number; max: number } {
  const nums = values.filter((v): v is number => v != null && Number.isFinite(v));
  if (nums.length < 2) return { d: "", min: 0, max: 0 };

  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const range = max - min || Math.abs(max) || 1;

  const toX = (i: number) =>
    values.length <= 1 ? W / 2 : (i / (values.length - 1)) * W;
  const toY = (v: number) =>
    H - Y_PAD - ((v - min) / range) * (H - 2 * Y_PAD);

  let d = "";
  let started = false;
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (v == null || !Number.isFinite(v)) continue;
    d += (started ? "L" : "M") + toX(i).toFixed(1) + " " + toY(v).toFixed(1);
    started = true;
  }

  return { d, min, max };
}

export default function MiniChart({ label, values, metric, decimals, suffix = "" }: MiniChartProps) {
  // Memoize path so stable values[] reference avoids recompute on every render
  const { d, min, max } = useMemo(() => buildPath(values), [values]);

  const nums = values.filter((v): v is number => v != null && Number.isFinite(v));
  const hasData = nums.length >= 2;

  const latest = hasData ? nums[nums.length - 1] : null;
  const first = hasData ? nums[0] : null;
  const delta = latest != null && first != null ? latest - first : null;

  // Line stroke color
  let stroke: string;
  if (metric === "net") {
    stroke = (delta ?? 0) >= 0 ? COLORS.net_pos : COLORS.net_neg;
  } else {
    stroke = COLORS[metric];
  }

  // Value color: net uses sign of latest, others use default text
  const valueColor =
    metric === "net" && latest != null
      ? latest >= 0
        ? COLORS.net_pos
        : COLORS.net_neg
      : "#e0e0e0";

  // Zero-line for net chart when zero is within the Y range
  let zeroLine: string | null = null;
  if (metric === "net" && hasData) {
    const range = max - min || Math.abs(max) || 1;
    const zeroY = H - Y_PAD - ((0 - min) / range) * (H - 2 * Y_PAD);
    if (zeroY >= 0 && zeroY <= H) {
      zeroLine = `M0 ${zeroY.toFixed(1)} L${W} ${zeroY.toFixed(1)}`;
    }
  }

  return (
    <div className="bg-bg border border-border rounded-md px-3 py-2.5 relative min-h-[110px]">
      <div className="text-muted text-[0.7rem] uppercase tracking-wider">{label}</div>

      {/* Latest value + delta */}
      <div className="text-[1.1rem] font-semibold mt-0.5 tabular-nums" style={{ color: valueColor }}>
        {latest != null ? (
          <>
            {fmt(latest, decimals)}
            {suffix}{" "}
            {delta != null && (
              <span
                className="text-[0.7rem] font-normal"
                style={{ color: delta >= 0 ? COLORS.net_pos : COLORS.net_neg }}
              >
                {delta >= 0 ? "+" : ""}
                {fmt(delta, decimals)}
              </span>
            )}
          </>
        ) : (
          <span className="text-muted">—</span>
        )}
      </div>

      {/* SVG polyline */}
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="block w-full mt-1.5"
        style={{ height: 60 }}
        aria-hidden="true"
      >
        {zeroLine && (
          <path
            d={zeroLine}
            stroke="#333"
            strokeWidth={0.5}
            strokeDasharray="2,3"
            fill="none"
          />
        )}
        {d && (
          <path d={d} stroke={stroke} strokeWidth={1.5} fill="none" />
        )}
      </svg>
    </div>
  );
}
